import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

export default function ProfileModal({ person, onClose }) {
  // Close on ESC
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!person) return null;

  function formatDescription(text = "") {
    // Split into blocks by blank lines
    return text.split(/\n\s*\n/).map((block) => {
      const lines = block
        .split(/\n/)
        .map((l) => l.trim())
        .filter(Boolean);

      // If all lines start with a bullet "•", render as a list
      if (lines.every((line) => line.startsWith("•"))) {
        return (
          <ul>
            {lines.map((line, i) => (
              <li key={i}>{line.replace(/^•\s*/, "")}</li>
            ))}
          </ul>
        );
      }

      // Otherwise render as a paragraph
      return <p>{lines.join(" ")}</p>;
    });
  }

  return createPortal(
    <Overlay aria-hidden="false" onClick={onClose}>
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-title"
        aria-describedby="profile-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <Header>
          <Title id="profile-title">{person.name}</Title>
          <CloseButton onClick={onClose} aria-label="Close modal">
            ×
          </CloseButton>
        </Header>

        <TopRow>
          <Avatar src={person.image} alt={`${person.name} profile photo`} />
          <Details>
            {person.role && (
              <Row>
                <Label>Role:</Label> <Value>{person.role}</Value>
              </Row>
            )}
            {person.team && (
              <Row>
                <Label>Responsibilty:</Label> <Value>{person.team}</Value>
              </Row>
            )}
            {person.profession && (
              <Row>
                <Label>Profession:</Label> <Value>{person.profession}</Value>
              </Row>
            )}
            <Row>
              <Label>Residence:</Label>{" "}
              <Value>{person.country || "Unknown"}</Value>
            </Row>
          </Details>
        </TopRow>

        <Body id="profile-desc">
          {formatDescription(
            person.longDescription ||
              person.description ||
              "No additional information provided."
          )}
        </Body>
      </Dialog>
    </Overlay>,
    document.body
  );
}

/* ====== Styles ====== */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: grid;
  place-items: center;
  z-index: 1000;
  padding: 16px;
`;

const Dialog = styled.div`
  width: 100%;
  max-width: 720px;
  max-height: 80vh;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: slideIn 200ms ease-out;

  @keyframes slideIn {
    from {
      transform: translateY(10px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: #f9fafb;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
`;

const CloseButton = styled.button`
  border: 0;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 8px;
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 20px;
  padding: 20px 24px 0;
  align-items: start;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Avatar = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  font-size: 15px;
`;

const Label = styled.span`
  font-weight: 700;
  color: #374151;
  min-width: 90px;
`;

const Value = styled.span`
  color: #111827;
`;

const Body = styled.div`
  padding: 20px 24px 40px;
  color: #333;
  line-height: 1.7;
  font-size: 15px;
  max-height: 50vh; /* 👈 limit height */
  overflow-y: auto; /* 👈 enable vertical scrolling */

  p {
    margin: 0 0 1.2em 0;
    text-align: justify;
    hyphens: auto;
  }

  ul {
    margin: 0 0 1.2em 20px;
    padding: 0;
  }

  li {
    margin-bottom: 0.5em;
    text-align: left;
  }
`;
